import { isProd } from "@/lib/utils";
import Head from "next/head";

export const getSeo = (path: string, title?: string, description?: string, image?: string) => {
  if (path.startsWith("/")) {
    path = path.slice(1);
  }

  if (path.includes("/")) {
    path = path.split("/")[0];
  }

  const content = {
    name: "DollarDeploy" + (isProd() ? "" : " (dev)"),
    title: "Deploy your app to production, no DevOps required",
    motto: "Your Apps. Your Infra. Zero DevOps.",
    description:
      "Get your app to production on a server you own, no DevOps required. Connect a GitHub repo and our AI deploys your Next.js, Python, Go, or Docker app in minutes, with automatic HTTPS and deploy on every git push.",
    keywords:
      "deploy app, no devops, self-hosting, Next.js hosting, deploy from GitHub, Vercel alternative, AI deploy, one-click deploy, deploy to your own server, VPS deployment, ship side project, deploy AI app, Cursor app deploy, localhost to production",
    image: "/cover.jpg"
  };

  if (path === "dashboard") {
    Object.assign(content, {
      title: "Dashboard",
      description: "Quick overview of your apps and hosts, and latest deployments.",
      image: "/api/og/dashboard"
    });
  }

  if (path === "settings") {
    Object.assign(content, {
      title: "Settings",
      description: "Settings for DollarDeploy",
      image: "/api/og/settings"
    });
  }

  if (path === "backup") {
    Object.assign(content, {
      title: "Backup",
      description: "Backups of your apps and hosts.",
      image: "/api/og/backup"
    });
  }

  if (path === "journal") {
    Object.assign(content, {
      title: "History",
      description: "Journal of activity and logs of your apps and hosts.",
      image: "/api/og/journal"
    });
  }

  if (path === "task") {
    Object.assign(content, {
      title: "Tasks",
      description: "Tasks for your apps and hosts.",
      image: "/api/og/task"
    });
  }

  if (path === "host") {
    Object.assign(content, {
      title: "Servers",
      description: "Servers you manage and deploy your apps to.",
      image: "/api/og/host"
    });
  }

  if (path === "app") {
    Object.assign(content, {
      title: "Application",
      description: "App details and management, backup, build and deployment.",
      image: "/api/og/app"
    });
  }

  if (path === "r") {
    Object.assign(content, {
      title: "Templates",
      description: "Deploy from templates in one click to your server.",
      image: "/api/og/r"
    });
  }

  if (path === "pricing") {
    Object.assign(content, {
      title: "Pricing",
      description:
        "Free plan to start, paid plans from $5/mo. No per-request fees, no surprise bills. Deploy your apps to a server you own.",
      image: "/api/og/pricing"
    });
  }

  if (path === "faq") {
    Object.assign(content, {
      title: "FAQ",
      description: "Frequently asked questions about DollarDeploy",
      image: "/api/og/faq"
    });
  }

  if (path === "privacy") {
    Object.assign(content, {
      title: "Privacy Policy",
      description: "Privacy Policy for DollarDeploy",
      image: "/api/og/privacy"
    });
  }

  if (path === "tos") {
    Object.assign(content, {
      title: "Terms of Service",
      description: "Terms of Service for DollarDeploy",
      image: "/api/og/tos"
    });
  }

  if (path === "about") {
    Object.assign(content, {
      title: "About us",
      description: "About us - DollarDeploy",
      image: "/api/og/team"
    });
  }

  if (title) {
    content.title = title;
  }

  if (description) {
    content.description = description;
  }

  if (image) {
    content.image = image;
  }

  return content;
};

export const SeoHead = ({
  path,
  title,
  description,
  image,
  children
}: {
  path: string;
  title?: string;
  description?: string;
  image?: string;
  children?: React.ReactNode;
}) => {
  const seo = getSeo(path, title, description, image);
  return (
    <Head>
      <title>
        {seo.title} - {seo.name}
      </title>
      <meta key="description" name="description" content={seo.description} />
      <meta key="keywords" name="keywords" content={seo.keywords} />
      <meta key="og:description" property="og:description" content={seo.description} />
      <meta property="og:title" content={`${seo.title} | DollarDeploy`} key="title" />
      {seo.image && <meta name="og:image" content={seo.image} key="image" />}
      {seo.image && <meta name="twitter:image" content={seo.image} key="twitter:image" />}
      {children}
    </Head>
  );
};
